import { AppstoreOutlined, ClockCircleOutlined, EditOutlined, EyeOutlined, FolderOpenOutlined, TableOutlined, ToTopOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Card, Col, Row, Tooltip, Button as Btn } from "antd";
import ServiceCard from "components/Home/ServiceCard/ServiceCard";
import VoucherCard from "components/Home/VoucherCard/VoucherCard";
import { useTranslation } from "contexts/TranslationContext";
import dayjs from "dayjs";
import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetPersonalInformationMutation, useGetUserAppsQuery, useGetPreAppStatusQuery } from "services/auth";
import { useGetBusinessProviderQuery } from "services/lookup";
import { Button } from "shared/components/Button";
import { Text17600 } from "shared/components/Text/Text";
import env from "shared/constants/env";
import * as LOCAL_STORAGE_KEYS from "shared/constants/localStorageKeys";
import routes, { appRoute } from "shared/constants/routes";
import RequestsFullCardsView from "../../components/Home/RequestsFullCardsView/RequestsFullCardsView";
import autoLeasing from "assets/svg/autoLeasing.svg";
import personalFinance from "assets/svg/personalFinance.svg";
import schoolAlhasa from "assets/svg/schoolAlhasa.svg";
import schoolAlKhobar from "assets/svg/schoolAlKhobar.svg";
import schoolAlRiyadh from "assets/svg/schoolAlRiyadh.svg";
import BgBottomLeftIcon from "assets/svg/bgBottomLeft.svg";

const getStatusLabel = (statusId) => {
	switch (statusId) {
		case "D":
			return "Draft";
		case "S":
			return "Submitted";
		default:
			return "-";
	}
};

const RequestsViewToggleButton = (props) => {
	const isGrid = useCallback(() => props?.state === "grid", [props?.state]);

	return <Button onClick={props?.toggleRequestsView} icon={!isGrid() ? <AppstoreOutlined /> : <TableOutlined />}></Button>;
};

const RequestsGridCard = (props) => {
	const data = props?.data || {};
	const navigate = useNavigate();
	const { translate } = useTranslation();
	const isDraft = useCallback(() => data?.status?.toLowerCase() === "draft", [data?.status]);

	return (
		<Card
			title={`${translate("HOME.APPL_NO")}: ${data?.APPLID}`}
			extra={<Tooltip title={isDraft() ? "Draft" : "Ready"}>{isDraft() ? <ClockCircleOutlined /> : <ToTopOutlined />}</Tooltip>}
			actions={[
				<Button
					icon={isDraft() ? <EditOutlined /> : <EyeOutlined />}
					onClick={() => {
						//view Application
						if (data.APPLICATION_STATUS === "S") {
							navigate(`/app/${routes.VIEW_REQUEST}/${data.APPLID}`);
						}
					}}
					type='text'>
					{isDraft() ? translate("HOME.COMPLETE") : translate("HOME.VIEW")}
				</Button>,
			]}>
			<Row gutter={[8, 8]}>
				<Col span={24}>
					{translate("HOME.PRODUCT")}: <strong>{data?.PRODUCT_NAME ? `${data?.PRODUCT_NAME} / ${data?.PRODUCT_NAME_AR}` : "-"}</strong>
				</Col>
				<Col span={24}>
					{translate("HOME.CONTRACT")}: <strong>{data?.CONTRACT_STATUS || "-"}</strong>
				</Col>
				<Col span={24}>
					{translate("HOME.STATUS")}: <strong className={isDraft() ? "text-yellow-500" : "text-green-600"}>{data?.status || "-"}</strong>
				</Col>
			</Row>
		</Card>
	);
};

const RequestsGridView = (props) => {
	const requests = props?.requests || [];

	if (Array.isArray(props?.requests) && props?.requests.length === 0) {
		return (
			<div className='mt-6 text-gray-300 text-center flex flex-col gap-2'>
				<div>
					<FolderOpenOutlined style={{ fontSize: 32 }} />
				</div>
				<em>No data</em>
			</div>
		);
	}

	return (
		<Row gutter={[16, 16]}>
			{requests.map((request, i) => (
				<Col key={i} sm={24} lg={8}>
					<RequestsGridCard data={request} />
				</Col>
			))}
		</Row>
	);
};

const FinanceProducts = ({ data }) => {
	const { translate, language } = useTranslation();
	const navigate = useNavigate();
	const sliderRef = useRef(null);
	const [activeSlide, setActiveSlide] = useState(0);
	const totalSlides = 2;
	const [user, setUser] = useState(null);
	const idStartsWithOne = user?.USER_ID?.toString().startsWith('1');

	useEffect(() => {
		// Fetch the user data from localStorage when component mounts
		const storedUser = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
		setUser(storedUser);
	}, []);


	const products = [
		{
			id: 1,
			title: translate("HOME.AUTO"),
			description: "HOME.AUTO_DESC",
			image: autoLeasing,
			route: 1
		},
		{
			id: 2,
			title: translate("HOME.PERSONAL"),
			description: "HOME.PERSONAL_DESC",
			image: personalFinance,
			route: 2
		}
	];

	const handleApplyNow = async (route) => {
		if (data?.length > 0 && Array.isArray(data) && data.some(item => item.CONTRACT_STATUS !== 'SIGNED')) {
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}

		try {
			const preAppStatus = await fetch(`${env.API_ENDPOINT}app/pre-app-status?id=${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID}`)

			const response = await preAppStatus.json()

			// console.log('pre', response)

			if (response?.result?.completedSteps.length >= 4 && response?.result?.status === 'COMPLETED') {
				navigate(appRoute(routes.PRODUCTS + `/${route}`));
			} else {
				navigate(appRoute(routes.PRE_APP));
				toast.error("Please complete pre-application steps and try again.");
			}
		} catch (error) {
			console.error("Pre-App Status error:", error);
			toast.error("Something went wrong in checking pre app status. Please try again.");
		}
	};

	const scrollLeft = () => {
		if (activeSlide > 0) {
			setActiveSlide(activeSlide - 1);
			sliderRef.current.scrollBy({ left: -300, behavior: 'smooth' });
		}
	};
	const scrollArLeft = () => {
		if (activeSlide < totalSlides - 1) {
			setActiveSlide(activeSlide + 1);
			sliderRef.current.scrollBy({ left: -300, behavior: 'smooth' });
		}
	};

	const scrollRight = () => {
		if (activeSlide < totalSlides - 1) {
			setActiveSlide(activeSlide + 1);
			sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
		}
	};
	const scrollArRight = () => {
		if (activeSlide > 0) {
			setActiveSlide(activeSlide - 1);
			sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
		}
	};

	// Handle scroll events to update active slide
	useEffect(() => {
		const handleScroll = () => {
			if (!sliderRef.current) return;

			const scrollPosition = sliderRef.current.scrollRight;
			const slideWidth = sliderRef.current.clientWidth;
			const newActiveSlide = Math.round(scrollPosition / slideWidth);

			if (newActiveSlide !== activeSlide && newActiveSlide >= 0 && newActiveSlide < totalSlides) {
				setActiveSlide(newActiveSlide);
			}
		};

		const sliderElement = sliderRef.current;
		if (sliderElement) {
			sliderElement.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (sliderElement) {
				sliderElement.removeEventListener('scroll', handleScroll);
			}
		};
	}, [activeSlide]);

	return (
		<div className="relative">
			<div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-6">
				{products.map((product) => (
					<div key={product.id} className="bg-[#E6F1F2] rounded-lg overflow-hidden flex flex-col md:flex-row">
						<div className="p-6 flex flex-col justify-between flex-1">
							<div>
								<h3 className="text-[#1F225B] text-2xl font-light mb-2">{product.title}</h3>
								<p className="text-[#8E9196] text-base mb-4">{translate(product.description)}</p>
							</div>
							<Btn
								// disabled={product.id === 2 && !idStartsWithOne}
								className="rounded-full bg-primary text-white font-semibold p-4 w-fit border border-primary hover:!border-primary hover:border-solid hover:!text-primary"
								onClick={() => handleApplyNow(product.route)}
							>
								{translate("HOME.CREATE_NEW")}

							</Btn>
						</div>
						<div className="md:w-2/5">
							<img src={product.image} alt={product.id} className="w-full h-full object-cover" />
						</div>
					</div>
				))}
			</div>
			<div className="md:hidden">
				<div
					ref={sliderRef}
					className="flex overflow-x-auto snap-x snap-mandatory gap-4 scrollbar-hide scroll-smooth"
					style={{
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						direction: language.code === 'ar' ? 'rtl' : 'ltr'
					}}
				>
					{products.map((product) => (
						<div key={product.id} className="bg-[#E6F1F2] rounded-lg overflow-hidden flex-shrink-0 w-full snap-center">
							<div className="p-6 flex flex-col justify-between">
								<div>
									<h3 className="text-[#1F225B] text-2xl font-light mb-2">{product.title}</h3>
									<p className="text-[#8E9196] text-base mb-4">{translate(product.description)}</p>
								</div>
								<Btn
									// disabled={product.id === 2 && !idStartsWithOne}
									className="rounded-full bg-primary text-white font-semibold p-4 w-fit  hover:!border-primary hover:border-solid hover:!text-primary"
									onClick={() => handleApplyNow(product.route)}
								>
									{translate("HOME.CREATE_NEW")}
								</Btn>
							</div>
							<div className="w-full">
								<img src={product.image} alt={product.id} className="w-full h-44 object-cover" />
							</div>
						</div>
					))}
				</div>

				{/* Navigation and indicator in horizontal layout - with RTL support */}
				<div className="flex items-center justify-center mt-4 space-x-3 rtl:space-x-reverse">
					{/* Left/Right Button (first button) */}
					<button
						onClick={language.code === 'ar' ? scrollArRight : scrollLeft}
						className={`
									flex items-center justify-center
									w-8 h-8 rounded-full
									transition-all duration-500 ease-in-out
									${(language.code === 'ar' ? activeSlide === 0 : activeSlide === 0)
								? 'bg-white border border-[#006e7e]'
								: 'bg-[#006e7e] border border-[#006e7e]'
							}
      `}
					>
						{language.code === 'ar' ? (
							<RightOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === 0 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						) : (
							<LeftOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === 0 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						)}
					</button>

					{/* Right/Left Button (second button) */}
					<button
						onClick={language.code === 'ar' ? scrollArLeft : scrollRight}
						className={`
									flex items-center justify-center
									w-8 h-8 rounded-full
									transition-all duration-500 ease-in-out
									${(language.code === 'ar' ? activeSlide === totalSlides - 1 : activeSlide === totalSlides - 1)
								? 'bg-white border border-[#006e7e]'
								: 'bg-[#006e7e] border border-[#006e7e]'
							}
     						`}
					>
						{language.code === 'ar' ? (
							<LeftOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === totalSlides - 1 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						) : (
							<RightOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === totalSlides - 1 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						)}
					</button>

					{/* Indicator line */}
					<div className="flex-grow h-1 bg-gray-200 rounded-full relative overflow-hidden">
						<div
							className="absolute top-0 h-full bg-[#006e7e] rounded-full transition-transform duration-500 ease-in-out"
							style={{
								width: `${(100 / totalSlides)}%`,
								[language.code === 'ar' ? 'right' : 'left']: 0,
								transform: `translateX(${language.code === 'ar' ? -activeSlide * 100 : activeSlide * 100}%)`
							}}
						></div>
					</div>
				</div>
			</div>
		</div>
	);
};

const SchoolProducts = ({ data }) => {
	const { translate, language } = useTranslation();
	const navigate = useNavigate();
	const sliderRef = useRef(null);
	const [activeSlide, setActiveSlide] = useState(0);
	const totalSlides = 3;
	const [user, setUser] = useState(null);

	useEffect(() => {
		// Fetch the user data from localStorage when component mounts
		const storedUser = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
		setUser(storedUser);
	}, []);

	const products = [
		{
			id: 1,
			title: translate("HOME.SCHOOL_ALHASA"),
			description: "HOME.AUTO_DESC",
			image: schoolAlhasa,
			route: 1
		},
		{
			id: 2,
			title: translate("HOME.SCHOOL_KHOBAR"),
			description: "HOME.PERSONAL_DESC",
			image: schoolAlKhobar,
			route: 2
		},
		{
			id: 3,
			title: translate("HOME.SCHOOL_RIYADH"),
			description: "HOME.PERSONAL_DESC",
			image: schoolAlRiyadh,
			route: 3
		}
	];

	const handleApplyNow = async (route) => {
		if (data?.length > 0 && Array.isArray(data) && data.some(item => item.CONTRACT_STATUS !== 'SIGNED')) {
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}

		try {
			const preAppStatus = await fetch(`${env.API_ENDPOINT}app/pre-app-status?id=${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID}`)

			const response = await preAppStatus.json()

			if (response?.result?.completedSteps.length >= 4 && response?.result?.status === 'COMPLETED') {
				navigate(appRoute(routes.SCHOOLS + `/${route}`));
			} else {
				navigate(appRoute(routes.PRE_APP));
				toast.error("Please complete pre-application steps and try again.");
			}
		} catch (error) {
			console.error("Pre-App Status error:", error);
			toast.error("Something went wrong in checking pre app status. Please try again.");
		}
	};

	const scrollLeft = () => {
		if (activeSlide > 0) {
			console.log("activeSlide scrollLeft", activeSlide)
			setActiveSlide(activeSlide - 1);
			sliderRef.current.scrollBy({ left: -300, behavior: 'smooth' });
		}
	};
	const scrollArLeft = () => {
		if (activeSlide < totalSlides - 1) {
			console.log("activeSlide scrollArLeft", activeSlide)
			setActiveSlide(activeSlide + 1);
			sliderRef.current.scrollBy({ left: -300, behavior: 'smooth' });
		}
	};

	const scrollRight = () => {
		if (activeSlide < totalSlides - 1) {
			console.log("activeSlide scrollRight", activeSlide)
			setActiveSlide(activeSlide + 1);
			sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
		}
	};
	const scrollArRight = () => {
		if (activeSlide > 0) {
			console.log("activeSlide scrollArRight", activeSlide)
			setActiveSlide(activeSlide - 1);
			sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
		}
	};

	// Handle scroll events to update active slide
	useEffect(() => {
		const handleScroll = () => {
			if (!sliderRef.current) return;

			const scrollPosition = sliderRef.current.scrollRight;
			const slideWidth = sliderRef.current.clientWidth;
			const newActiveSlide = Math.round(scrollPosition / slideWidth);

			if (newActiveSlide !== activeSlide && newActiveSlide >= 0 && newActiveSlide < totalSlides) {
				setActiveSlide(newActiveSlide);
			}
		};

		const sliderElement = sliderRef.current;
		if (sliderElement) {
			sliderElement.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (sliderElement) {
				sliderElement.removeEventListener('scroll', handleScroll);
			}
		};
	}, [activeSlide]);

	return (
		<div className="relative">
			<div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-4">
				{products.map((product) => (
					<div key={product.id} className="bg-[#E6F1F2] rounded-lg overflow-hidden flex flex-col md:flex-row">
						<div className="p-6 flex flex-col justify-between flex-1">
							<div>
								<h3 className="text-[#1F225B] text-2xl font-light mb-2">{product.title}</h3>
								<p className="text-[#8E9196] text-base mb-4">{translate(product.description)}</p>
							</div>
							<Btn
								// disabled
								// className="rounded-full !bg-blue-950
								// !text-white cursor-not-allowed font-semibold p-4 w-fit"
								 className="rounded-full bg-primary text-white font-semibold p-4 w-fit"
								onClick={() => handleApplyNow(product.route)}
							>
								{translate("VOUCHERS.APPLY_NOW")}
							</Btn>
						</div>
						<div className="md:w-2/5">
							<img src={product.image} alt={product.id} className="w-full h-full object-cover" />
						</div>
					</div>
				))}
			</div>

			{/* Mobile view with slider */}
			<div className="md:hidden">
				<div
					ref={sliderRef}
					className="flex overflow-x-auto snap-x snap-mandatory gap-4 scrollbar-hide scroll-smooth"
					style={{
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						direction: language.code === 'ar' ? 'rtl' : 'ltr'
					}}
				>
					{products.map((product) => (
						<div key={product.id} className="bg-[#E6F1F2] rounded-lg overflow-hidden flex-shrink-0 w-full snap-center">
							<div className="p-6 flex flex-col justify-between">
								<div>
									<h3 className="text-[#1F225B] text-2xl font-light mb-2">{product.title}</h3>
									<p className="text-[#8E9196] text-base mb-4">{translate(product.description)}</p>
								</div>
								<Btn
									// disabled
								// 	className="rounded-full !bg-blue-950
								// !text-white cursor-not-allowed font-semibold p-4 w-fit"
									className="rounded-full bg-primary text-white font-semibold p-4 w-fit"
									onClick={() => handleApplyNow(product.route)}
								>
									{translate("VOUCHERS.APPLY_NOW")}
								</Btn>
							</div>
							<div className="w-full">
								<img src={product.image} alt={product.id} className="w-full h-44 object-cover" />
							</div>
						</div>
					))}
				</div>

				{/* Navigation and indicator in horizontal layout - with RTL support */}
				<div className="flex items-center justify-center mt-4 space-x-3 rtl:space-x-reverse">
					{/* First navigation button */}
					<button
						onClick={language.code === 'ar' ? scrollArRight : scrollLeft}
						className={`
							flex items-center justify-center
							w-8 h-8 rounded-full
							transition-all duration-500 ease-in-out
							${(language.code === 'ar' ? activeSlide === 0 : activeSlide === 0)
								? 'bg-white border border-[#006e7e]'
								: 'bg-[#006e7e] border border-[#006e7e]'
							}
						`}
					>
						{language.code === 'ar' ? (
							<RightOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === 0 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						) : (
							<LeftOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === 0 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						)}
					</button>

					{/* Second navigation button */}
					<button
						onClick={language.code === 'ar' ? scrollArLeft : scrollRight}
						className={`
							flex items-center justify-center
							w-8 h-8 rounded-full
							transition-all duration-500 ease-in-out
							${(language.code === 'ar' ? activeSlide === totalSlides - 1 : activeSlide === totalSlides - 1)
								? 'bg-white border border-[#006e7e]'
								: 'bg-[#006e7e] border border-[#006e7e]'
							}
						`}
					>
						{language.code === 'ar' ? (
							<LeftOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === totalSlides - 1 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						) : (
							<RightOutlined
								className="transition-colors duration-500 ease-in-out"
								style={{
									color: activeSlide === totalSlides - 1 ? '#006e7e' : 'white',
									fontSize: '15px'
								}}
							/>
						)}
					</button>

					{/* Indicator line - adjusted for 3 slides */}
					<div className="flex-grow h-1 bg-gray-200 rounded-full relative overflow-hidden">
						<div
							className="absolute top-0 h-full bg-[#006e7e] rounded-full transition-transform duration-500 ease-in-out"
							style={{
								width: `${(100 / totalSlides)}%`,  // Width now 33.33% for 3 slides
								[language.code === 'ar' ? 'right' : 'left']: 0,
								transform: `translateX(${language.code === 'ar' ? -activeSlide * 100 : activeSlide * 100}%)`
							}}
						></div>
					</div>


				</div>
			</div>
		</div>
	);
};

export default function Home() {
	const [user, setUser] = useState({});
	// const [view, setView] = useState("fullCard");
	const { data, isLoading, refetch } = useGetUserAppsQuery(JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID);
	const [fetchPersonalInfo, { isLoading: personalInfoLoading }] = useGetPersonalInformationMutation();

	const { data: businessProviders } =
		useGetBusinessProviderQuery();

	const { translate, language } = useTranslation();

	const columnsV1 = [
		{
			title: translate("OFFER_TABLE.APP_NO"),
			dataIndex: "APPLID",
			key: "APPLID",
			sorter: () => true,
		},
		{
			title: translate("OFFER_TABLE.PRODUCT_NAME"),
			dataIndex: "productName",
			key: "productName",
			sorter: () => true,
		},
		{
			title: translate("OFFER_TABLE.CONTRACT"),
			dataIndex: "contractStatus",
			key: "contractStatus",
			sorter: () => true,
		},
		{
			title: translate("OFFER_TABLE.STATUS"),
			dataIndex: "status",
			key: "status",
			sorter: () => true,
		},
	];

	// const actionsv1 = [
	// 	{
	// 		id: 0,
	// 		icon: FileTextOutlined,
	// 		label: translate("OFFER_TABLE.CONTRACT"),
	// 		actionKey: "Contract",
	// 	},
	// 	{
	// 		id: 1,
	// 		icon: TagsOutlined,
	// 		label: translate("OFFER_TABLE.OFFER"),
	// 		actionKey: "Offer",
	// 	},
	// 	// {
	// 	// 	id: 2,
	// 	// 	icon: ReloadOutlined,
	// 	// 	label: translate("OFFER_TABLE.REFRESH"),
	// 	// 	actionKey: "refresh",
	// 	// },
	// 	{
	// 		id: 2,
	// 		icon: DeleteOutlined,
	// 		label: translate("OFFER_TABLE.DELETE"),
	// 		actionKey: "Delete",
	// 	},
	// 	// {
	// 	// 	id: 4,
	// 	// 	icon: FileTextOutlined,
	// 	// 	label: translate("OFFER_TABLE.VIEW_CONTRACT"),
	// 	// 	actionKey: "View Contract",
	// 	// },
	// 	{
	// 		id: 3,
	// 		icon: EditOutlined,
	// 		label: translate("OFFER_TABLE.EDIT"),
	// 		actionKey: "Edit",
	// 	},
	// ];

	// const toggleRequestsView = useCallback(() => {
	// 	setView((view) => (view === "grid" ? "table" : "grid"));
	// }, []);

	const navigate = useNavigate();

	let tableData = data?.map((row) => ({
		...row,
		productName: row?.PRODUCT_NAME ? `${row?.PRODUCT_NAME} / ${row?.PRODUCT_NAME_AR}` : "-",
		contractStatus: row?.CONTRACT_STATUS ? row?.CONTRACT_STATUS : "-",
		status: getStatusLabel(row?.APPLICATION_STATUS),
	}));

	useEffect(() => {
		let user;

		try {
			user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER));

			user.APP_CREATED = false;

			localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));
			setUser(user);
		} catch (e) {
			console.error(e);
		}
	}, [data]);

	useEffect(() => {
		setUser(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER)));

		refetch();
	}, []);

	const onDelete = (appId) => {
		fetch(env.API_ENDPOINT + `app/delete-app?appId=${appId}`, {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
			},
		})
			.then((res) => res.json())
			.then((res) => {
				if (res?.result?.deleted) {
					toast.success("App has been deleted.");
					refetch();
				} else {
					toast.error("Server Error");
				}
			});
	};
	const onEdit = (applId) => {
		navigate(appRoute(routes.CREATE_REQUEST + "/" + routes.CREATE_REQUEST_LOAN_DETAILS) + `?appl_=${applId}`);
	}

	const handleCreateNewApplication = async () => {
		if (data?.length > 0 && Array.isArray(data) && data.some(item => item.CONTRACT_STATUS !== 'SIGNED')) {
			toast.error(translate("APPLICATION_ALREADY_EXISTS"));
			return;
		}

		try {
			const preAppStatus = await fetch(`${env.API_ENDPOINT}app/pre-app-status?id=${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.IDB_USR_ID}`)

			const response = await preAppStatus.json()

			// console.log('pre', response)

			if (response?.result?.completedSteps.length >= 4 && response?.result?.status === 'COMPLETED') {
				navigate(appRoute(routes.CREATE_REQUEST + "/" + routes.CREATE_REQUEST_LOAN_DETAILS));
			} else {
				navigate(appRoute(routes.PRE_APP));
				toast.error("Please complete pre-application steps and try again.");
			}
		} catch (error) {
			console.error("Pre-App Status error:", error);
			toast.error("Something went wrong in checking pre app status. Please try again.");
		}
	};

	// if (data) {
	// 	try {
	// 		const response = await fetchPersonalInfo({ id: `${JSON.parse(localStorage.getItem("@@TAMWEEL_USER"))?.USER_ID}` }).unwrap().catch((res) => {
	// 			// console.log("Personal info err resp:", res);
	// 		});

	// 		// route to load-details with appl id and dispatch
	// 		if (response?.payload?.data?.address?.personNationalAddressInfo?.Addresses) {
	// 			navigate(appRoute(routes.CREATE_REQUEST + "/" + routes.CREATE_REQUEST_LOAN_DETAILS));
	// 		}
	// 		else {
	// 			toast.error(response?.payload?.data?.address?.error?.message || 'Please update your national address and try again');
	// 		}
	// 	} catch (error) {
	// 		//for now this will be error for personalInfo
	// 		// console.log("Personal info resp er:", error);
	// 		return toast.error(translate("Something went wrong"));
	// 	}
	// } else {
	// 	return toast.error(translate("Something went wrong"));
	// }


	const noRequests = () => {
		if (!tableData || tableData.length === 0) {
			return (
				<div className={'no-result'}>
					<img src={'https://res.cloudinary.com/dlg8sln4t/image/upload/v1727442315/Group_3_okh2yo.png'} />
					<p>{translate('HOME.NO_REQUESTS')} </p>
				</div>
			)
		}
	}

	return (
		<>
			{/* Bottom-left image */}
			{/* <img
				src={BgBottomLeftIcon}
				alt="Bottom Left Decoration"
				className="absolute bottom-0 w-2/5 md:w-2/5 lg:w-2/5 -z-10"
			  /> */}
			{/* Header Section */}
			<div className="relative">
				<div className="container mx-auto py-12 px-4">
					<h1 className="text-3xl text-[#101010] font-bold mb-2">
						{translate("HOME.Hi")},{" "}
						<strong>{user?.FIRST_NAME}</strong>!
					</h1>
					{user?.LAST_LOGIN && (
						<div className="text-[#878787] text-lg mb-6">
							{translate("HOME.LAST_LOGIN")}:{" "}
							<strong>{dayjs(user?.LAST_LOGIN).format("dddd, D MMM ")}</strong>
						</div>
					)}

					<h1 className="text-3xl md:text-5xl text-[#5F5F5F]  mt-12 font-extralight">
						{translate("HOME.PRODUCT_TITLE")}
					</h1>
				</div>
			</div >

			<div className="container mx-auto pb-10 px-4" >
				<FinanceProducts data={data} />
			</div>

			<div className="container mx-auto px-4">
				<h1 className="text-3xl md:text-5xl text-[#5F5F5F] mt-12 font-extralight mb-12">
					{translate("HOME.SCHOOL_PRODUCTS_TITLE")}
				</h1>
			</div>
			<div className="container mx-auto pb-10 px-4" >
				<SchoolProducts data={data} />
			</div>

			<div className="container mx-auto px-4">
				<h1 className="text-3xl md:text-5xl text-[#5F5F5F] mt-12 font-extralight mb-6">
					{translate("HOME.CARDS_TITLE")}
				</h1>
			</div>
			{/* Services Section */}
			< div className="container mx-auto pb-10 px-4" >
				<VoucherCard data={data} />
			</div >

			{/* Request Section */}
			< section >
				<div className="bg-white rounded border p-6">
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-2xl font-bold">
							{translate("HOME.REQUEST")}
						</h2>
					</div>

					{<RequestsFullCardsView onEdit={onEdit} onDelete={onDelete} requests={tableData} />}
					{!isLoading && noRequests()}
				</div>
			</section >
		</>
	);
}

